import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { Route, BrowserRouter as Router } from 'react-router-dom';

import WithoutPega from './WithoutPega';
import PegaChatWidget from './components/PegaChatWidget.tsx';
import { ChatProvider } from './context/ChatContext.tsx';
import { MergedConfigProvider } from './context/MergedConfigProvider.tsx';
import { TranslationsProvider } from './context/TranslationsContext.tsx';
import { MainJourney } from './journey/MainJourney.tsx';
import { getConfig } from './translations/getTranslationConfig.ts';
import type { Language } from './translations/translationsTypes.ts';
import { getDefaultLanguageByRegion } from './utils/defaultLanguageByRegion.ts';
import { injectBigScript } from './utils/injectBigScript.ts';
import { getJourneyAvailability } from './utils/journeyAvailability.ts';
import logger from './utils/logger.ts';
import { resolveQueryRegion } from './utils/resolveQueryRegion.ts';

let journeyId = new URLSearchParams(window.location.search).get('jid');

if (!journeyId) {
  console.log('No journeyId provided in URL, using default styles');
  journeyId = 'spectre';
}

const searchParams = new URLSearchParams(window.location.search);
const journeyAvailability = getJourneyAvailability(journeyId, searchParams);
const journeyBlocked = !journeyAvailability.allowed;

switch (journeyId) {
  case 'spectre':
  case 'spectreretail':
    await import('./themes/spectre.css');
    break;
  case 'uk1':
  case 'uk2':
  case 'uk3':
  case 'uk4':
  case 'uk5':
  case 'uk6':
  case 'uk7':
  case 'ukall':
  case 'ie1':
  case 'eu1':
  case 'eu2':
  case 'sg1':
  case 'sg2':
  case 'sg3':
  case 'sg4':
  case 'sg5':
  case 'sg6':
  case 'sgaddon':
  case 'sgall':
  case 'spreadbet':
  case 'gtv':
  case 'global':
    await import('./themes/map.css');
    break;
  default:
    if (journeyBlocked) {
      await import('./themes/spectre.css');
    } else {
      throw new Error(`Unknown journeyId: ${journeyId}`);
    }
}

// Import these AFTER the theme to ensure CSS variables are defined
await import('rc-drawer/assets/index.css');
await import('./index.css');

const queryClient = new QueryClient();

(async () => {
  if (journeyBlocked) {
    createRoot(document.getElementById('container') as HTMLElement).render(null);
    return;
  }

  // language and region should be dynamic eventually
  const embedded = new URLSearchParams(window.location.search).get('embedded');
  const searchParams = new URLSearchParams(window.location.search);
  // use the last 'l' param value to match old demo onboarding behavior;
  // otherwise default from query region (eu1/eu2), then en
  const langValues = searchParams.getAll('l');
  const queryRegion = resolveQueryRegion(searchParams);
  const language = (searchParams.get('language') ||
    langValues[langValues.length - 1] ||
    getDefaultLanguageByRegion(queryRegion) ||
    'en') as Language;
  const region = 'gb';

  const translationsPromise = getConfig(language);

  if (!embedded) {
    injectBigScript(language, region);
  }

  const root = createRoot(document.getElementById('container') as HTMLElement);
  const debugEnv = import.meta.env.VITE_CUSTOM_ENV;
  const loadApp = import.meta.env.VITE_LOAD_APP;

  if (loadApp === 'false') {
    logger.info(`Onboarding Gateway app loading is disabled in ${debugEnv} environment.`);
    return;
  }

  root.render(
    <ChatProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <Route
            exact
            path="/"
            render={() => (
              <MergedConfigProvider journeyId={journeyId}>
                <TranslationsProvider prefetchedPromise={translationsPromise}>
                  <MainJourney journeyId={journeyId} />
                </TranslationsProvider>
              </MergedConfigProvider>
            )}
          />
          <Route exact path="/without-pega">
            <WithoutPega />
          </Route>
          <PegaChatWidget />
        </Router>
      </QueryClientProvider>
    </ChatProvider>
  );

  /**
   * -------------------------------------------------------------
   * CloudWatch RUM Initialization
   * -------------------------------------------------------------
   *
   * We intentionally delay initialization of AWS CloudWatch RUM.
   *
   * Why?
   * RUM uses AWS Cognito to obtain temporary credentials before it
   * can send telemetry. When initialized during app bootstrap, this
   * causes a network request to Cognito (e.g. cognito-identity.eu-west-1)
   * which can add several seconds of latency for users far from the
   * region (e.g. Australia).
   *
   * To prevent this request from impacting initial page load or
   * rendering performance, we defer RUM initialization until:
   *
   * 1. The page has fully loaded (`window.load`)
   * 2. The browser is idle (`requestIdleCallback`) so it won't compete
   *    with rendering or user interaction
   *
   * We also dynamically import the RUM module so it is code-split and
   * not included in the critical startup execution path.
   *
   * This ensures:
   * - RUM does not block React rendering
   * - Cognito authentication runs in the background
   * - User experience and performance metrics (LCP/FCP) are unaffected
   *
   * NOTE:
   * If modifying this logic, ensure RUM is NOT reintroduced into the
   * application bootstrap path.
   */

  window.addEventListener('load', () => {
    const startRum = () => {
      // Dynamically import the RUM module so it loads after the app
      import('./rum').then(({ initRum }) => initRum());
    };

    // requestIdleCallback allows the browser to run this task
    // when it has spare CPU time after rendering and other work.
    if ('requestIdleCallback' in window) {
      (
        window as Window & {
          requestIdleCallback: (callback: () => void) => void;
        }
      ).requestIdleCallback(startRum);
    } else {
      // Safari does not support requestIdleCallback, so fall back
      // to a small delay to ensure the page is already interactive.
      setTimeout(startRum, 2000);
    }
  });
})();
